/**
 * Single source of truth for every feel-critical number.
 *
 * Rules for anyone editing this file:
 *  - Every value is in SI units (m, kg, s, rad, N) unless the name says otherwise.
 *  - Never hardcode a feel number in a system; put it here so it can be swept and diffed.
 *  - Values are grouped by owning system so parallel edits rarely collide.
 */

export const Tuning = {
  bike: {
    /** Rider + bike, kg. Enduro bike ~15kg, rider ~75kg. */
    mass: 92,
    /** Distance front axle to rear axle, m. */
    wheelbase: 1.24,
    /** Height of combined CoM above ground at sag, m. */
    comHeight: 1.06,
    /**
     * Fraction of static weight on the rear wheel. This is not applied as a fudge: the
     * front axle sits this fraction of the wheelbase ahead of the CoM, which produces
     * the split through the moment balance, and rider weight shift then moves the CoM
     * inside that span. Change it and the static sag split follows.
     */
    rearWeightBias: 0.56,
    /** Head tube angle from vertical, rad. 64deg HTA = 26deg from vertical. */
    headAngle: (90 - 64) * (Math.PI / 180),
    /** Fork offset, m. Trail = (R*sin(head) - offset) / cos(head). */
    forkOffset: 0.044,
    /** Trail of the reference geometry above, m — self-steer is scaled against it so
     * that changing head angle or offset actually changes how the bike holds a line. */
    refTrail: 0.132,
    wheelRadius: 0.371,
    /**
     * Wheel-envelope sample spacing as a fraction of wheel radius — the offsets of the
     * inner and outer pair of probes that stand in for the wheel's own curvature.
     *
     * Two pairs, not one. A single pair at 0.55 R leaves the hull faceted: the tread
     * between 0.2 m and the full 0.37 m of reach is unrepresented, so a lip whose crest
     * sits in that band arrives as a step and the wheel is fired off it. Measured on the
     * descend run, the bike was airborne for 58% of the ride and every touchdown pegged
     * a wheel at the force ceiling. Adding the outer pair at 0.85 R (the last offset
     * where the wheel still has meaningful ground clearance — the drop is 175 mm there)
     * makes the wheel start climbing a square edge half a wheel early, which is what a
     * real 29" wheel does and is most of why one rolls over chunder that would stop a
     * 26" wheel dead.
     */
    envelopeSpan: 0.45,
    envelopeSpanOuter: 0.85,
    /** Softness of the max over those probes, m. Small enough that a probe which really
     * is 50 mm proud still wins outright; large enough that two probes within a
     * centimetre of each other blend instead of swapping the contact normal between
     * steps. Zero here is a hard max, and a hard max makes a stationary bike hum. */
    envelopeBlend: 0.025,
    /** Wheel + tire rotational inertia, kg*m^2. */
    wheelInertia: 0.14,
    /**
     * How much of the chassis's pitch/lean rate reaches the hubs as fore/aft velocity.
     *
     * It has to match how much of that rotation the *geometry* already puts into the
     * contact patch position, and the geometry here is rigid — solveWheel resolves the
     * patch through the full pitched arms. So this is 1.0, and it exists as a knob only
     * because getting it wrong is invisible until you measure: a patch position that
     * swings with pitch and a patch velocity that does not is a tire spring with no damper
     * in that mode. Measured at 0, the harness read a bike parked on a 15% slope ringing
     * at 6 Hz forever, both slip readouts pinned at 1.0, walking 400 mm downhill every
     * three seconds with both levers pulled.
     *
     * Below 1.0 would be the argument that a telescopic fork and a swingarm turn most of a
     * chassis pitch into travel rather than axle surge — true of a real bike, and the right
     * thing to model the day the suspension gets its own kinematics. It is not true of this
     * one, and agreeing with the geometry matters more than being half-right twice.
     */
    patchRateCoupling: 1.0,
    /**
     * Roll inertia about the contact line, kg*m^2. A 92 kg mass at 1.06 m is 103 on its
     * own, so this is close to physical. Dropping it to make the bike flick faster makes
     * lean respond to countersteer in a tenth of a second, which reads as a twitch, not
     * as a rider.
     */
    /** How much of the rigid-geometry CoM drop a lean actually produces (see physics).
     * 1.0 is the pure two-wheeler; a rider counter-leaning keeps far more height than that. */
    leanDropFraction: 0.5,
    rollInertia: 88,
    yawInertia: 46,
    pitchInertia: 64,
    /**
     * Frontal area * drag coefficient, m^2. This is what caps a fire-road descent at a
     * believable speed instead of 90 km/h, and it is the only *steady* loss the bike has
     * that is worth arguing about, so it has to be a real number rather than a
     * convenient one.
     *
     * A rider sitting bolt upright measures about 0.9; one in the attack position an
     * enduro rider holds on a descent — elbows out, chest low, weight over the cranks —
     * measures 0.65-0.75. 0.95 was the upright figure and it cost 95 N at 13 m/s, most of
     * a real bike's *entire* parasitic loss twice over, which is a large part of why
     * nothing here carried momentum. 0.80 is the honest average of the two, since the
     * rider is neither tucked nor sitting up the whole way down: 76 N at 13 m/s, and with
     * 27 N of rolling resistance an 18% fire road settles around 58 km/h. Fast, inside the
     * band a real descent runs at, and still short of terminal on anything steeper — which
     * is what leaves the brakes something to do.
     */
    dragArea: 0.80,
    /**
     * Suspension linkage: the fraction of a tire's longitudinal force that is reacted
     * through the frame rather than through the spring. Chain tension pulls on the
     * swingarm and brake torque is taken by the caliper mount, so their line of action
     * passes above the contact patch and only part of the couple reaches the springs.
     *
     * Without antiSquat the geometry is brutally literal: full pedal is exactly the
     * wheelie limit (a = g * b / h = 0.51 g here), so every run-up tops the fork out and
     * the front tire stops existing. antiDive is deliberately small — a telescopic fork
     * barely resists dive, and that dive is how the player reads how close the front
     * brake is to a stoppie, so removing it would cost more than it buys. It is not,
     * however, allowed to be *zero* support: at 0.12 with the softer front spring the
     * fork bottomed on every 1 g stop, and a fork that is already on its bump stop cannot
     * tell the player anything about the next 20% of lever.
     */
    antiSquat: 0.6,
    antiRise: 0.4,
    antiDive: 0.16,
    /** Residual yaw/pitch damping while grounded, 1/s. Stands in for everything the
     * two-contact-point model does not resolve (tire scrub, chassis flex). */
    yawDamping: 0.8,
    pitchDamping: 7.0,
    /** Lean / pitch past which the rider is on the floor, rad. */
    crashLean: 1.25,
    crashPitch: 1.25,
    /** How long past those angles before the rider is actually down, s. */
    crashHoldTime: 0.22,
    /**
     * How far above the heightfield, as a fraction of `comHeight`, the chassis is not
     * allowed to sink. Was 0.3.
     *
     * This is a numerical guard, and where it sits has to agree with the wheel model or it
     * becomes a defect of its own. Travel is kinematic, so the deepest position the solver
     * can still describe is the one where the whole stack is consumed —
     * `comHeight + sagRef - (travel + stopTravel)`, about 0.50 of comHeight here. The old
     * 0.3 let the CoM sit 190 mm below that, and down there the projection of the
     * chassis-to-ground gap onto the pitched body axis comes out NEGATIVE: both wheels
     * report no contact, every suspension and tire force goes to zero, `airborne` reads
     * true, and the bike is held up by this clamp alone while buried in the dirt.
     * Measured on `holdline` — 0.28 s of it at 3.1 km/h, ending in 1376 mm past full
     * travel and 30.9 g the moment the attitude swung far enough for contact to resume.
     *
     * 0.55 rather than exactly 0.50 so the guard engages just before the geometry runs
     * out rather than exactly on it.
     *
     * Round 6: this is still where the frame meets the hillside, but it is no longer a
     * clamp. `bellySpring` below turned it into a contact. See physics.ts's `bellyLoad`.
     */
    chassisFloorFraction: 0.55,
    /* --- The frame against the hillside -------------------------------------------
     *
     * Raising `chassisFloorFraction` 0.3 -> 0.55 made the burial shallower and did not
     * remove it, and the reason it could not is that a positional clamp is not a contact.
     * It teleported the CoM and deleted the velocity that took it there, both outside the
     * force sum — so `gForce` could not see it, `updateImpact` could not score it, and the
     * measured state was a bike sitting on the guard with both wheels reporting no contact
     * and `airborne` reading true. These three numbers are that clamp rewritten as an
     * ordinary spring-damper contact along the ground normal.
     *
     * Sized from what the thing has to do rather than fitted, and swept, because the first
     * two values were both wrong in the same direction. The frame must hold the bike's own
     * weight (902 N) on a stroke small enough to be invisible: at 220 kN/m that is 4 mm,
     * two orders below the 170 mm the suspension works over, so nothing about ride height
     * or sag can notice it. The damper only ever resists closing motion, so this element is
     * dissipative and cannot hand energy back the way the old clamp's `-0.15` restitution
     * did — that restitution was energy from nowhere and it is gone.
     *
     * The sweep, measured at 240 Hz on the gate scenario (dwell = share of the 90900 sim
     * steps with the CoM at or below this height; over100 = share with a wheel more than
     * 100 mm past full travel, which is exactly how deep the DRAWN wheel is in the dirt):
     *
     *      old hard clamp   dwell 0.22 %   over100 0.08 %   ghost 0.11 %   crashes 1
     *      45 kN/m          dwell 2.15 %   over100 0.20 %   ghost 0.05 %   crashes 2
     *      90 kN/m          dwell 1.22 %   over100 0.22 %   ghost 0.06 %   crashes 0
     *      220 kN/m         see the round-3 report
     *
     * A soft frame contact is not a gentler version of the clamp, it is a different defect:
     * the bike settles into it and dwells instead of being answered and stood back up, and
     * every millimetre it sinks is a millimetre of extra `overTravel`, i.e. a millimetre
     * further the renderer has to draw the wheel underground. Stiff and heavily damped
     * (zeta ~1.5 on the 30 kg chassis, so it cannot ring at 240 Hz) is what a frame is.
     *
     * `bellyMaxForce` matches `suspension.maxWheelForce` deliberately: a frame on rock and
     * a wheel on rock are the same event and there is no reason for one to be able to make
     * more force than the other. At 12 kN the spring has used 55 mm, still inside
     * `bellyBackstopSlack`, so the positional guard below it stays unreachable by ordinary
     * means — which is the whole point of splitting them.
     *
     * `bellyMaxForce` matches `suspension.maxWheelForce` deliberately: a frame on rock and
     * a wheel on rock are the same event and there is no reason for one to be able to make
     * more force than the other. It is 13 g, and a rider who puts the frame into the
     * hillside hard enough to reach it should feel every one of them.
     *
     * `bellyBackstopSlack` is how far below the contact the positional guard at the bottom
     * of `step` sits. It has to be larger than the stroke the spring actually uses or the
     * guard would be the load path again by the back door; 60 mm is three times the 20 mm
     * of static deflection.
     */
    bellySpring: 220000,
    bellyDamping: 8000,
    bellyMaxForce: 12000,
    bellyBackstopSlack: 0.06,
    /* --- Where the renderer actually puts the wheels ------------------------------
     *
     * Wave 5's gap: the drawn wheel is >=200 mm inside the hillside on 5.98% of `fullrun`
     * frames at ordinary speeds, and `overTravel` — the metric that should catch it —
     * reads zero through most of it. The reason it reads zero is that the sim and the
     * renderer do not agree about where the bike is, and until this round nothing in the
     * project measured the difference.
     *
     * `bikeModel.ts` poses the bike about the **contact line**: the root goes at
     * `(s.x, s.y - comHeight, s.z)` and the chassis is rotated about it. `solveWheel`
     * resolves the contact patch about the **CoM**: the patch is `comHeight*cosL` below
     * and `offset` ahead, and rotating that arm swings the patch fore/aft by
     * `comHeight*sin(pitch)` and outboard by `comHeight*sin(lean)`.
     *
     * Those are two different points, and not by a little. At the worst frame of the gate
     * scenario (t=38 s, 30 km/h, pitch -25 deg, lean 30 deg) the sim samples the ground
     * 0.71 m behind and 0.47 m to the side of where the wheel is drawn, and it decides
     * how much suspension travel the wheel has from the height there. On a mountain made
     * of 40-70% faces that is easily half a metre of height error, so the wheel solve
     * holds the chassis up at a height that is right for a piece of hillside the player
     * cannot see, and the wheel is drawn into the one they can.
     *
     * `drawnPoseBlend` is the A/B knob: 0 reproduces the old CoM-pivot sampling exactly,
     * 1 samples and measures travel where the wheel is drawn. The moment arms are NOT
     * blended — `rLong`/`rUp`/`rLat` stay the rigid-body arms about the CoM, because that
     * is a statement about the dynamics and it is correct. What changes is only which
     * piece of ground the wheel is standing on.
     *
     * Measured 0 -> 1 on `fullrun` inside one browser session: worst drawn-wheel sink
     * 336 mm -> 30 mm, frames over the 50 mm budget 2.94% -> 0.00%, peak g 9.18 -> 6.93,
     * airborne 0.62% -> 0.20%, and **completion 16.7% -> 99.8% with zero ejections**. On
     * `corner`, mean sink over the whole run 45.5 mm -> 0.0 mm. See the round-7 block in
     * physics.ts's header for the mechanism and for the earlier A/B on the pre-fix trail
     * network, where the same change cost completion because the burial was filtering the
     * trail's own defects.
     *
     * `drawnChainstay` MIRRORS the local `CHAINSTAY` constant in `src/render/bikeModel.ts`
     * (0.437), which is not in this file because the sim has no opinion about it. It is
     * needed here because the renderer's chassis origin is the bottom bracket, not the
     * CoM: the two are `wheelbase*rearWeightBias - (wheelbase - drawnChainstay)` = 109 mm
     * apart, which is 46 mm of height error at 25 degrees of pitch — most of a sink
     * budget on its own. If bikeModel's CHAINSTAY ever changes and this does not,
     * `captures/ride-feel-tools/sink.mjs` reports the mirror error directly (it reads the
     * posed `frontAxle`/`rearAxle` bones out of the scene graph and differences them
     * against what this file predicts), so the drift is caught rather than assumed.
     */
    drawnPoseBlend: 1,
    drawnChainstay: 0.437,
    /**
     * How long `ridefeel.wheelSinkDepth` stops measuring for around a crash, s. **0.**
     *
     * It was 1.2 and it is now zero, and the reason it is a constant rather than deleted
     * is that the invariant it feeds — `ridefeel.sinkUnmeasured` — must be able to fail.
     *
     * Round 10 stood the sink measurement down while the bike was down, because
     * `bikeModel.ts` raises the drawn root out of the hillside there and this file's model
     * of the pose did not include that lift. It published the hole honestly, and the hole
     * was 16-22% of every scenario in the delivered set that crashes: one sim step in five
     * on which nothing in the project was measuring whether the bike was drawn underground.
     * The other half was meant to be `rider.crashBikeSinkM`, which reports only on the
     * frames where a crash actually happened and therefore sits in the report's "declared
     * but never observed" list the rest of the time — so a reader could not tell a covered
     * run from an uncovered one.
     *
     * Round 11 mirrored the lift in `measureSink` instead (see CRASH_LIFT_IN in
     * physics.ts). The measurement now runs on every step of every run and reads the frame
     * the player is shown, crash included. Setting this back to 1.2 reproduces the old
     * blindness exactly, which is how the coverage invariant is shown to be an instrument
     * and not a decoration: `captures/ride-feel-tools/bury.mjs` reads it at 0.0% here and
     * at the historical figure there, in one process, on ground nothing else can move.
     */
    sinkBlindS: 0,
  },

  suspension: {
    frontTravel: 0.17,
    rearTravel: 0.16,
    /* --- The tire ------------------------------------------------------------------
     *
     * There was no tire in this model until round 3. The frame's stroke was measured
     * straight off the heightfield, which says a 2.5" casing at 22 psi is a rigid link —
     * and it is the softest spring on the bike, in series with everything else.
     *
     * Two things it buys, and only the second is obvious. It adds stroke that costs
     * nothing: at the 3-4 kN a landing puts through a patch the casing gives 25-35 mm,
     * and that is 25-35 mm the fork does not have to find. And because it sits *below*
     * the fork it is the only element that can take the first millisecond of a square
     * edge, which is exactly what riders change pressure and inserts for.
     *
     * Rates are the real vertical stiffness of a 29 x 2.5 at trail pressure — front 22
     * psi, rear 25 — measured figures for a modern casing, not a fitted number. They are
     * an order above the springs, so nothing about sag or small-bump compliance changes:
     * at the 400-500 N a parked bike puts through a patch the casing gives 3-4 mm.
     *
     * `tireMaxDeflect` is where the rim reaches the ground. Past it the series compliance
     * stops and the fork takes everything again, which is what a rim strike is.
     */
    frontTireRate: 118000,
    rearTireRate: 142000,
    tireMaxDeflect: 0.042,
    /**
     * Spring rate at the wheel, N/m. Static load over these gives the ride height:
     * 0.44*mg = 397 N over 9800 is 41 mm, 24% of the 170 mm fork; 0.56*mg = 505 N over
     * 11300 is 45 mm, 28% of the 160 mm shock.
     *
     * They went UP, from 6900/9300, and this is the round's main change. The previous
     * pair was sized for 34% sag at both ends on the argument that a softer spring at
     * more sag makes less force at the end of the stroke. That is true and it was the
     * wrong trade, for a reason the drop bench made unarguable: a bike sitting a third of
     * the way into its stroke has only two thirds of it left, so *every* hit ran out of
     * frame travel and was handed to the rider stage. Measured, a 3.1 m/s touchdown — a
     * 0.5 m drop, an ordinary trail roller — put both wheels on the bump stop and read
     * 6.2 g. 34% is also simply not a fork number: a 170 mm fork runs 15-20% sag and a
     * shock 28-30%, and the difference is exactly the ride height the bike was missing.
     *
     * The end-of-stroke force this would have cost is given back below: the main spring
     * is now linear and the ramp is small and late, so despite the stiffer rate the force
     * at full travel is 1666 N front (was 1502 + 850 of ramp = 2352). Stiffer everywhere
     * it matters, softer exactly where it sets peak g.
     *
     * The rear went up again, 11300 -> 12400, and it is the only spring number this round
     * touched. The reason is an asymmetry the capture set is unambiguous about: pooled over
     * every scenario the *rear* sits at full travel three times as often as the front
     * (2.07% of grounded frames against 0.73%), which is what you would expect of the wheel
     * that carries 56% of the weight on 10 mm less stroke. Priced on the two benches that
     * are deterministic — the mountain plunge is chaotic enough that a 5% speed change at
     * ten seconds puts the bike somewhere else entirely by forty, so it cannot price
     * anything this small — it takes rear time on the stop from 304 ms to 221 ms on the
     * 0.25 m/9 m rollers and 633 ms to 575 ms on the 0.45 m/14 m ones, for +0.05 g there,
     * +0.1 g at the 4 and 6 m/s drops, and +0.10 g of small-bump chatter. Sag goes 28% to
     * 25.4%, which is still inside the band a shock is set up in.
     */
    frontRate: 9800,
    rearRate: 12400,
    /**
     * Progression of the *main* spring — the rate multiplier at full travel, before the
     * end-stroke ramp below.
     *
     * 1.0. Not "mild progression": none, and that is a measurement, not an aesthetic.
     * Peak g on a landing is set by how flat the force profile is across the stroke, and
     * progression is the one shape that guarantees the peak lands at the end, where the
     * wheel has already spent its travel and the force can only hurt. On the drop bench,
     * taking the pair from 1.28/1.2 to 1.0 was worth 0.3-0.5 g at every touchdown speed
     * above 3 m/s and cost nothing anywhere. Everything progression was there for — the
     * stroke feeling like it is running out, resistance to bottoming — is now the ramp's
     * job, where it can be made dissipative and where its knee can sit at the end of the
     * stroke instead of being smeared across all of it.
     */
    frontProgression: 1.0,
    rearProgression: 1.0,
    /**
     * Mid-stroke support, N — the hump an air spring's negative chamber and a volume
     * spacer put between sag and the end of the stroke, and where in the stroke it sits.
     *
     * This is deliberately not `progression`. Progression multiplies the rate, so it puts
     * its largest contribution at full travel, which is exactly where more force is only
     * more peak g — that is what the measurement behind `frontProgression: 1.0` above
     * found and it still stands. This term instead ramps in over `supportStart..supportEnd`
     * and then *plateaus*, so it raises the force through the middle of the stroke and
     * adds the same amount at 90% as at 100%: the rate at full travel is untouched.
     *
     * The capture asked for it. Binned by travel decile, grounded frames on the descend
     * line were bimodal — a quarter in the first tenth, half in the second to fourth, and
     * then more frames *bottomed* (14%) than in the entire top half of the stroke put
     * together. That is a spring that is too soft in the middle: a hit passes straight
     * through the region that should catch it and arrives at the stop instead.
     *
     * Hysteretic, because a conservative version of this term stored the extra energy and
     * gave it straight back — on the drop bench it took restitution from 0.29 to 0.49, and
     * a bike that bounces off its own mid-stroke is worse than one that bottoms.
     *
     * Size and placement are a straight trade and the bench prices it. It shipped at
     * 400/450 over 0.44..0.86 and that was too small by a factor of three, for a reason
     * the trace makes concrete rather than statistical: on the mountain-plunge bench the
     * *rear wheel carries the whole bike on its own* for seconds at a time — the terrain
     * pitches down faster than the chassis does, the front hangs, and the rear is asked
     * for 3.5-4.5 kN quasi-statically. At 400/450 the whole stroke could only make
     * 2.7-2.8 kN, so the answer to "carry 4 kN" was "sit on the stop until the ground
     * lets go", which is precisely the 17-20% of grounded frames this round is about. It
     * was never mainly a landing problem.
     *
     * 1050/1200 — two and a half times the size, in exactly the same place. The placement was
     * swept again (0.30/0.38/0.44/0.50 starts against 0.66/0.78/0.86 ends) and 0.44..0.86
     * won on the one axis that pulls the other way: bringing the hump in early enough to
     * bite just past sag triples the rate where small bumps live, and on the 60 mm/3 m
     * chatter bench a 0.30 start took the bike from 2% of the run airborne to 12% — the
     * opposite of "small bumps disappear". Starting at 0.44 leaves the first 75 mm of the
     * fork exactly as soft as it was and still puts ~1.9 kN under the wheel at 70% of the
     * stroke and ~2.7 kN at the end, which is where the load actually has to be carried.
     * Sag (24% front, 28% rear) is below the start, so ride height and the static split
     * are untouched, and the hump is fully in by 86%, so the rate at full travel is
     * unchanged — which is the whole reason this is a plateau and not progression.
     *
     * Priced on the bench with the rest of the round, against the shipped shape: time at
     * full travel on the plunge 4.34% -> 1.8%, the worst single episode 1004 ms -> 263 ms,
     * and time on the stop across the washboard set 3.8 s -> 1.1 s. Swept on its own,
     * 900 -> 1400 moves plunge time on the stop 2.15% -> 1.54% and costs 0.6-0.9 g on
     * every flat drop. 1050 sits at the low end of that on purpose: swept against
     * `frontCompQuad`, buying the last of the stop time with damper rather than with
     * spring is strictly better — 1200/135 beats 1350/105 on peak g, on landing g, on
     * chatter *and* on time at full travel, because a damper is flat in position and a
     * spring is not. That trade is the round's brief:
     * bottom-outs are supposed to be felt and rare, and they were neither.
     *
     * Still hysteretic, and more load-bearing than before now that the hump is three
     * times the size: a conservative version of this term took drop-bench restitution
     * from 0.29 to 0.49, and a bike that bounces off its own mid-stroke is worse than one
     * that bottoms.
     */
    frontSupport: 1050,
    rearSupport: 1200,
    supportStart: 0.44,
    supportEnd: 0.86,
    supportHysteresis: 0.4,
    /**
     * Damping, N*s/m — low-speed and high-speed split at the knee velocity. Critical
     * damping for this sprung mass on these springs is ~1100 front / ~1500 rear, so
     * these sit near 0.45 critical on compression and 0.7 on rebound. Three times
     * critical (which is what "big number = planted" gives you) makes every square edge
     * a spike through the frame and hides the springs completely.
     */
    /**
     * The rebound pair went up (760/400 front, 1020/520 rear) and could only go up because
     * of `rebLoadFloor`. Rebound damping is what stops a landing being handed back, and it
     * is also — without the floor — what unloads the tire, because a damper that outguns
     * the spring clamps the whole wheel force to zero. Those two used to be the same
     * number and it had to be small. With the floor guaranteeing the wheel keeps a quarter
     * of its spring force whatever the damper asks for, this can be sized purely for
     * restitution: on the drop bench 760/400 -> 950/560 takes a 5 m/s landing's coefficient
     * of restitution from 0.37 to 0.30 and a 2 m/s one from 0.24 to 0.08, for no peak g at
     * all and 8 ms more at the stop. ~0.86 of critical on the low-speed circuit is a firm
     * but ordinary trail setting.
     */
    frontCompLow: 520,
    frontCompHigh: 210,
    frontRebLow: 950,
    frontRebHigh: 560,
    rearCompLow: 700,
    rearCompHigh: 280,
    rearRebLow: 1270,
    rearRebHigh: 730,
    /** Shaft speed where damping transitions from low- to high-speed circuit, m/s. */
    kneeVelocity: 0.28,
    /**
     * Quadratic high-speed compression, N*s^2/m^2, applied across the whole stroke.
     *
     * Sized off the measurement that drove this whole round. A chute ridden at 65 km/h
     * produces ~4.5 m/s shaft speeds; at 4.5 m/s this is worth ~1.1 kN per wheel, which
     * is enough to keep that hit inside the stroke instead of pushing through into the
     * rider stage — and because a damper's force does not depend on position, that
     * kilonewton is there from the first millimetre rather than only at the end where it
     * cannot help. At the 0.3 m/s of ordinary trail chatter the same term is worth 0.2 N,
     * so nothing about small-bump compliance changes.
     *
     * 62/70 -> 105/118 -> 135/150 over two rounds, and now back to 120/133. The reason it
     * had to keep climbing was that it was the term keeping fast hits inside the frame
     * stroke, and the frame stroke was all the travel the model had. It is not any more —
     * `rider.riderMass` is — so this term went back to being sized for what a damper is
     * actually for, and the drop bench prices the difference: with the rider carrying the
     * landing, 135 -> 120 together with a lower `blowoffSpeed` takes the 4 m/s touchdown
     * from 7.3 g to 6.3 g and the 5 m/s one from 9.1 g to 7.1 g, and both stay off the
     * bump stop. Below about 110 the plunge starts turning back up — the deepest excursion
     * past full travel goes 15 mm to 96 mm at 60 — so this sits just above that knee.
     *
     * The reason to keep it at all is unchanged and is the strongest argument in the file:
     * it is the only force here that is flat in position, so it is the only one that can be
     * worth 0.2 N at the 0.3 m/s of ordinary chatter and a kilonewton at the 4.5 m/s a
     * chute at 65 km/h produces. That is exactly the "small bumps disappear, big hits use
     * travel" split the bar asks for, and no spring shape can produce it.
     */
    frontCompQuad: 120,
    rearCompQuad: 133,
    /**
     * Blow-off shaft speed, m/s: past this the shim stack is open and damping force stops
     * growing. Applies to the high-speed linear and the quadratic circuits alike.
     *
     * Not modelled for fidelity — modelled because without it the damper becomes the
     * largest force in the file on exactly the hits it can least help with. Measured
     * before it existed: a wheel arriving at the 8 m/s velocity clamp made 1.6 kN of
     * linear high-speed plus 3.7 kN of quadratic on top of 3.8 kN of spring, pinned the
     * per-wheel guard, and read 27 g at the CoM. Real shim stacks blow off for the same
     * reason: a damper that keeps growing with velocity turns a fast hit into a wall.
     *
     * Where it sits is a genuine trade and the drop bench settles it. The round-1 note
     * here argued the value should be low because a damper's force is largest at first
     * contact "where it does nothing but raise the peak". That is only true when first
     * contact *is* the peak, and measured, it is not: on a 5 m/s touchdown the force at
     * first contact was 1.7 g and the peak was 8.9 g, 90 ms later and 300 mm deeper. A
     * damper is flat in position, which is the profile that minimises peak load, and it
     * is the only dissipative term in the file — force it removes is gone rather than
     * stored and handed back. Raising the blow-off from 2.0 to 2.9 was worth 0.8-1.7 g on
     * every touchdown between 3 and 5.5 m/s.
     *
     * It stops at 3.6 for the other half of the trade: past that the damper alone starts
     * to *become* the peak on the biggest hits — at 4.5 the 5.3 m/s touchdown went back
     * up from 6.0 to 6.9 g, because the two wheels were making 5 kN at first contact
     * before either had moved. Between 3.2 and 4.0 the whole curve is flat to within a
     * tenth of a g; 3.6 is the middle of that plateau.
     *
     * It could only come this far up because of `compDeepFade` below. Without the deep
     * bleed, raising blow-off past ~2.9 hurt the fastest landings, because the same
     * circuit that was helping at first contact was still making its full force at the
     * end of the stroke where it could only add to the peak. Separating the two — full
     * authority at the top of the stroke, faded at the bottom — is what makes a fast
     * damper and a low peak compatible. Ordinary chatter is untouched either way: at the
     * 0.3 m/s shaft speed of small bumps this circuit is not in play at all.
     *
     * 3.6 -> 4.6 -> 3.8, and the round-3 move back down is the clearest single consequence
     * of giving the model a rider. 4.6 m/s of shaft speed before a shim stack opens is not
     * a fork number — real high-speed compression blows off between 1.5 and 2.5 — and it
     * was up there for one reason: it was holding fast hits inside the frame stroke,
     * because the frame stroke was the only travel in the model. Measured with `riderMass`
     * carrying the landing instead, the damper at first contact is pure peak: dropping
     * 4.6 -> 4.0 takes the 5 m/s touchdown from 9.1 g to 7.3 g on its own, and 4.6 -> 3.8
     * with the matching `frontCompQuad` reads 7.1 g and does not reach the stop at all.
     *
     * It stops at 3.8 rather than going to a fork-realistic 2.5 for the reason the note
     * above gives, which still holds: past the bottom of this range the biggest hits get
     * deeper faster than the peak comes down. Swept 2.6/3.2/3.4/3.6/3.8/4.0/4.6, the
     * deepest excursion past full travel on the plunge is 108/35/87/77/15/75/63 mm — noisy,
     * because that bench is chaotic, but the trend below 3.4 is not.
     */
    blowoffSpeed: 3.8,
    /**
     * Position-sensitive compression bleed: where in the stroke the compression circuit
     * starts fading (fraction of travel) and what fraction of it survives at full travel.
     * Rebound is deliberately untouched — holding the wheel down after a deep stroke is
     * the anti-pogo mechanism and it is needed most exactly where this fades.
     *
     * See the long note in solveWheel. Short version: peak load is set by how flat the
     * force profile is, and a damper still making its full blow-off force at the end of
     * the stroke is adding a kilonewton to the one place the profile is already highest.
     * Measured on the drop bench, the damper was a third of the peak force on a 5.3 m/s
     * touchdown, at 170 mm of travel where it could no longer prevent anything.
     *
     * The surviving fraction went 0.35 -> 0.25 with this round's larger `frontCompQuad`.
     * The bleed's whole argument is "do not add a kilonewton where the profile is already
     * highest", and the circuit being bled is now half again as big, so the same argument
     * asks for a deeper fade. Measured: it takes 0.8 g off the worst landing in the plunge
     * and 0.4 g off the median one, and costs 7 ms of time at full travel.
     */
    compFadeStart: 0.45,
    compDeepFade: 0.25,
    /** Rebound's own blow-off, m/s — deliberately far higher. Holding the wheel down
     * after a deep stroke is the whole anti-pogo mechanism, and a rebound circuit that
     * opens at the compression side's 2 m/s hands the landing straight back. */
    blowoffSpeedReb: 5.5,

    /* --- The end-stroke ramp ------------------------------------------------
     *
     * This replaces what used to be here: a bumper, a second "rider" spring stage, and a
     * hard `maxNormalForce` ceiling that every one of them ran into. That ceiling was the
     * single worst number in the file, and not for the obvious reason.
     *
     * The obvious reason is that a clamp is not a spring: once a wheel reached 4200 N at
     * about 80% of the stroke, the force stopped rising, so *nothing resisted the
     * remaining 20%*. Measured over the wave-1 captures, 78% of landings reached full
     * travel and 38% put both wheels there at once — which is what you would predict
     * from a suspension whose last fifth of travel is free.
     *
     * The non-obvious reason is worse. A clamp truncates the compression stroke far more
     * than the rebound stroke, because compression is where the damper's contribution is
     * added and rebound is where it is subtracted. So the clamp was quietly deleting most
     * of the hysteresis loop — the area between the two branches *is* the energy the
     * suspension eats. Measured: a landing arriving at 5.7 m/s left the ground again at
     * 3.4 m/s, a coefficient of restitution of 0.6. That is a trampoline, and it is why
     * 55% of landings kicked the bike back off the ground inside a quarter of a second
     * and why a third of every descent was spent airborne.
     *
     * What is here instead is one continuous progressive curve that starts at `rampStart`
     * of the fork stroke and runs through the end of the stroke *and* the rider's own
     * travel, because those two are the same ramp: as the fork runs out, the rider's legs
     * and arms take over, and their resistance ramps too. Nothing clamps it. The
     * `maxNormalForce` pair below still exist, but only as numerical guards — they now
     * sit far outside anything ordinary riding reaches.
     */

    /**
     * Fraction of the stroke at which the ramp begins. Before it the spring is nearly
     * linear so small bumps disappear; after it the rate climbs continuously.
     *
     * It went to 0.58 in round 1 because the ramp was being used for mid-stroke support:
     * with a 6900 N/m fork, full front brake at 1 g put 1168 N through the front wheel
     * and a linear spring answered by diving 169 mm of its 170. Mid-stroke support is now
     * the spring rate's job — the same 1168 N over 9800 N/m is 119 mm, which is a fork
     * working hard and not a fork on its stop — so the ramp goes back to being an
     * end-stroke ramp, which is where a real bumper is and where its knee costs least.
     */
    rampStart: 0.78,
    /**
     * Force the end-stroke ramp adds by full travel, N — segment 1.
     *
     * Much smaller than it was (850/900), because with the stiffer linear main spring the
     * ramp is no longer holding the bike up and every newton it adds lands squarely on
     * the peak. It exists now for one reason: so that running out of stroke is something
     * the player feels arriving rather than a shelf they discover afterwards. Over the
     * last 37 mm it is worth 420 N with a rate reaching ~22 kN/m, which is an
     * unmistakable firming without being the largest term in the landing.
     *
     * The measurement that sets the size: on the drop bench, 150 N and 850 N of ramp were
     * within 0.15 g of each other at every touchdown speed, so the cue is close to free —
     * but it is only free while the main spring is linear. Piled on top of a progressive
     * spring (which is what the 850 N version sat on) the same ramp cost 0.5 g. Re-swept
     * this round with the rider stage engaging inside the stroke: 300 -> 500 was worth
     * 0.4 g at 3 m/s and bought 13 ms off the stop, and past 500 the g grew faster than
     * the time came down.
     *
     * 420/450 -> 200/220 now that the mid-stroke plateau exists. The ramp's only remaining
     * job is the *cue* — the last 37 mm firming so running out of stroke is something the
     * player feels arriving — and the plateau, and now the rider's own legs, have taken
     * over holding the wheel up. Every newton the ramp adds lands on the peak by construction, and
     * measured against the plateau, dropping it from 200 to 120 changed time on the stop by
     * 2 ms and peak g by 0.1, so 200 N buys the cue for nothing.
     */
    frontRampForce: 200,
    rearRampForce: 220,
    /**
     * Where the bump stop starts, as a fraction of *frame* travel.
     *
     * This was "the rider's own stage" for two rounds and it is not the rider any more.
     * Round 3 gave the model an actual rider — 62 kg on a real load path, see
     * `rider.riderMass` — so the thing living past the end of the fork stroke went back to
     * being what it physically is: an elastomer bumper, and then whatever else in the
     * frame, wheel and casing deforms before the axle is against the hillside.
     *
     * That reclassification is what let it be re-sized honestly. As "the rider" it had to
     * be soft and enormously long (600 mm) because it was standing in for a body absorbing
     * a landing; every millimetre of that length was also a millimetre the chassis sank
     * below where the renderer draws the wheel, which is why the capture set had the bike
     * 300-450 mm inside the ground on its worst hits. As a bumper it is short and firm,
     * and the sinking goes with it.
     *
     * 0.88 of the stroke — 150 mm of 170 front, 141 of 160 rear — is where a real fork's
     * bumper starts to touch. Swept 0.86/0.90/0.94 on the plunge with everything else
     * fixed: 0.94 reads 0.46% of grounded frames on the stop with 7 episodes, 0.90 reads
     * 0.29% with 4, 0.86 reads 0.25% with 2 and costs 0.2 g. The whole band is inside a
     * quarter of a g on the drop bench because with the rider's mass on the legs the
     * springs are no longer what stops a landing.
     */
    stopStart: 0.88,
    /**
     * The bumper's plateau force, N, and the stroke over which it gets there.
     *
     * A bumper is not a spring with a rate — it is a lump of microcellular urethane that
     * takes load almost immediately and then squashes at roughly constant force until it
     * is solid. `1-(1-v)^2` over `stopSatTravel` is that shape, and the saturation scale
     * is what makes it a bumper rather than a mattress: at 180 mm (what it was when this
     * was "the rider") the term made 98 N where the fork ran out, which is why wheels sank
     * 450 mm past full travel and read `travel == travelMax` for every millisecond of it.
     * At 60 mm it makes 2.0 kN front by the end of the frame stroke.
     *
     * 2700/3000 N against 1400/1540. It could go up by a factor of two, and had to, for
     * exactly the reason the previous note said it could not: the number that used to
     * bound it was peak g on a landing, and a landing is no longer the springs' problem.
     * With 62 kg of the 92 on the rider's legs, the drop bench reads 4.7/7.3/9.1/9.1 g at
     * 3/4/5/6 m/s whether this is 2400 or 3400. What it does change is how deep and how
     * long an excursion past full travel is: swept 2400/2600/2800/3400 on the plunge,
     * deepest excursion 74/60/59/40 mm and worst episode 171/154/154/121 ms. Past ~3000 it
     * starts costing washboard peak g (6.8 -> 9.0 at 3400) because the bumper is then stiff
     * enough to be the largest force in a square-edge hit, which is the failure the whole
     * `maxNormalForce` note below is about.
     */
    frontStopForce: 2700,
    rearStopForce: 3000,
    stopSatTravel: 0.06,
    /**
     * The bumper going solid, N at the end of `stopTravel` — quadratic in how far past the
     * frame stroke the wheel is.
     *
     * Urethane bottoms out too. Quadratic so its rate is zero where it joins the plateau
     * (no step) and largest where it hands over to `hardContactSpring` (no shelf), which
     * is what makes the whole profile monotone from sag to the hillside with nowhere for a
     * wheel to park.
     *
     * 1800/2000 over the shorter `stopTravel` is a rate reaching ~15 kN/m at the end,
     * against ~4 kN/m when the same force was spread over 600 mm. Still an order below the
     * hillside term it joins, which is the constraint — this must firm up, not stop the
     * bike; stopping the bike is what the hillside is for and it is deliberately rare.
     */
    frontStopEnd: 1800,
    rearStopEnd: 2000,
    /**
     * Deep-stroke rebound damping, N*s/m at full engagement. Linear, and large.
     *
     * This is the anti-pogo term. Coming back out of a deep stroke the ramp force is
     * enormous and it is all stored energy; without a rebound circuit sized against it,
     * the suspension hands the whole landing back and fires the bike off the ground
     * again. A real fork does exactly this with a rebound needle — a hard bottom-out
     * comes back slowly and deliberately, and the bike stays planted.
     *
     * It came down from 4300/4700 because at those numbers it was not damping the rebound,
     * it was deleting it. 1.2 kN*s/m of engagement against a 2 m/s extension is 2.4 kN
     * opposing a 2 kN spring, so the wheel force clamped to zero and stayed there for the
     * whole recovery — measured on descend, both wheels read 0 N while pinned at full
     * travel for 100 ms. A wheel making no force cannot launch the bike, so this looked
     * like anti-pogo; it is the same mechanism as having no suspension at all, and it is
     * what left the bike with no normal load to hold a lean with after a landing.
     * `rebLoadFloor` now makes that impossible whatever this number is, and with the floor
     * in place the drop bench puts 2400 within 5 ms and 0.02 g of 4300 on every touchdown
     * up to 5.7 m/s while returning noticeably sooner off the biggest ones.
     */
    frontRampRebDamping: 2400,
    rearRampRebDamping: 2650,
    /**
     * The most a rebound damper is allowed to take away, as the fraction of the
     * spring-like force at that position which must survive.
     *
     * A damper cannot pull a tire off the ground. Travel in this model is kinematic — it
     * is wherever the chassis is relative to the ground — so after a landing throws the
     * chassis up, the solver reads a fast extension and asks the rebound circuit for the
     * force that goes with it. On real hardware the question is the other way round: the
     * fork extends *because* its spring drives the wheel down, so the fastest it can
     * extend is where damping equals spring, and past that the wheel falls behind the
     * chassis rather than being hauled up into it with the tire still on the dirt.
     *
     * 0.33 says the wheel keeps a third of its spring force through any rebound. On the
     * drop bench it removes the light-load window entirely — 38 ms of both wheels under a
     * quarter of static load after a 5 m/s landing becomes 0 ms — for 0.00 g. It also sets
     * how briskly the chassis is pushed back out of a deep stroke, which is the other half
     * of what this round was sent back for: at 0.25 the capture showed a wheel sitting at
     * full travel carrying 600 N (correct, but only just enough to climb out), and each
     * step up shortens the worst episodes at the cost of restitution — 0.45 was worth
     * 50 ms off an 8.5 m/s landing and 0.07 of bounce, which is the wrong side of the
     * trade. 0.33 is the knee.
     */
    rebLoadFloor: 0.33,
    /**
     * Fraction of the ramp force returned on the way back out.
     *
     * A spring is conservative; an air spring with seals and a bottom-out elastomer are
     * not. Real damper hysteresis is 10-20%; this is more than that because a urethane
     * bumper's own loss is large, and because the one thing a landing must not do is give
     * the energy back. The rider's share of that dissipation is no longer in here — it is
     * `rider.legDamping`, where it belongs, and it is much the bigger half.
     */
    rampHysteresis: 0.45,
    /**
     * How far past full fork travel the stop keeps going before the hillside, m.
     *
     * 0.58 -> 0.22, and this is the number the previous round got most wrong, for a reason
     * it stated in its own note: "absorbing 5.7 m/s in the 165 mm the frame has is 10 g no
     * matter how cleverly it is shaped... There is no way to hit the bar without this
     * stage being long." That arithmetic is correct and the conclusion drawn from it was
     * not. The extra stroke a landing needs does have to exist, but it belongs to the
     * *rider*, above the bike, not to the wheel below it — and putting it below the wheel
     * had a cost the note never priced. Travel here is kinematic, so every millimetre past
     * full travel is a millimetre the chassis sits below where the renderer draws the
     * wheel. At 0.58 the capture set had the bike 300-450 mm inside the hillside on its
     * worst hits, which is exactly the "no bike falling through terrain" line in the bar.
     *
     * With `rider.riderMass` carrying the landing, what is left here is a bumper and the
     * frame, wheel and casing flexing behind it. Measured on the plunge, the deepest
     * excursion past full travel goes 102 mm to 15 mm, and every bench in the file is
     * *identical to three decimal places* for any value of this between 0.22 and 0.50 —
     * because with `frontStopForce` making 2.7 kN inside the first 60 mm, ordinary riding
     * simply never gets out here any more.
     *
     * So what sets it is not ordinary riding, it is the pathological event, and 0.22 was
     * measured wrong. The capture caught it: `braking` puts the front wheel into a
     * roughly 390 mm ledge 27 m from the spawn with the rear wheel already in the air, and
     * at 0.22 the whole of the remaining stroke is inside the case-severity span, so a
     * terrain step scored a full-severity case and a crash. Eighteen of twenty-five riding
     * scenarios ended there. That step is a defect in the surface — the wave-3 coherence
     * pass measured the same profile going 7% to 60% inside one metre — and turning a
     * defect in someone else's piece into an unrecoverable crash in mine is the wrong
     * failure mode. 0.40 leaves the same event a hard case: speed scrubbed, balance shaken,
     * rider authority docked for a beat, and rideable out of.
     *
     * It is still deliberately part of `force` rather than the hillside term: a bottomed
     * suspension really is pressing the tire into the ground, so it has to load the
     * contact patch and make the same moments every other suspension force makes.
     */
    stopTravel: 0.40,
    /** Past full travel *and* past the rider it is a hillside, not a spring. Stiff,
     * damped going in, and a rebound term large enough to cancel the spring coming out
     * (no restitution).
     *
     * The damping term was the whole story behind the 10-20 g spikes the harness kept
     * reading. It multiplies shaft speed, which is clamped at 8 m/s, so any fast
     * bottom-out pinned the force at the numerical ceiling in one 4 ms step — and
     * because a bank's normal is nearly horizontal, that ceiling arrives as pure
     * deceleration: one nose-first landing took 18 km/h off the bike in 0.3 s and the
     * corner run never moved again. Softer, and the same hit becomes a bottom-out the
     * rider rides out of. */
    /**
     * The hillside past the end of everything. Softer and shorter-armed than it was
     * (40000/1500), because with 430 mm of ramp in front of it this is now only reached
     * by genuinely driving an axle into a cut bank, and when it is reached it must stop
     * the bike rather than rebound it: measured at the old numbers a wheel hit the guard
     * at 13 kN and threw the bike back up at 6 m/s, which then landed again — the whole
     * distribution of "landings" in a capture was largely one impact ringing.
     */
    hardContactSpring: 22000,
    hardContactDamping: 900,
    hardContactRebound: 15000,
    /**
     * How far past full frame travel the axle is against the MOUNTAIN, m. Was `stopTravel`.
     *
     * This is round 11's change and it is a decoupling, not a retune. Until this round the
     * hillside term engaged at `stopTravel`, so the same constant set two unrelated things:
     * how long the case-severity ramp is, and how deep the chassis may sink before anything
     * hard stops it. The note on `stopTravel` above argues at length for 0.40 and every word
     * of that argument is about the FIRST of those — "at 0.22 the whole of the remaining
     * stroke is inside the case-severity span, so a terrain step scored a full-severity case
     * and a crash. Eighteen of twenty-five riding scenarios ended there."
     *
     * The second one it never priced, and it is this piece's own fatal budget: travel is
     * kinematic, so every millimetre past full travel is a millimetre the chassis sits
     * below where the renderer draws the wheel. 400 mm of bumper against a 50 mm
     * `SINK_BUDGET_MM` is an eightfold disagreement between two constants in the same file.
     * Measured on the deterministic bench (`captures/ride-feel-tools/bury.mjs`, an analytic
     * ledge, no mountain and no other agent), a 0.5 m square-edged ledge at 38 km/h put the
     * DRAWN front wheel 270 mm inside the hillside — and the bike rode away from it.
     *
     * `stopTravel` still spans the severity ramp, so the case a terrain step scores is
     * unchanged; only the geometry is bounded. 0.05 is `SINK_BUDGET_MM` written as a
     * length: the hillside starts exactly where the drawn wheel starts being visibly
     * inside it, which is the only defensible place to put it.
     *
     * Swept on the bench. Nothing below a 2 m drop to flat moves AT ALL at any value of
     * this — those rows are identical to the digit from 0.03 to 0.40 — because ordinary
     * riding never leaves the frame's own stroke. What moves is only the events that were
     * already past it:
     *
     *              0.5 m ledge @ 38 km/h       3.5 m huck to flat
     *      0.40    270 mm sink,  9.6 g          56 mm,  14.0 g      <- the old behaviour
     *      0.09    127 mm sink, 15.1 g          56 mm,  14.0 g
     *      0.05     46 mm sink, 15.2 g          55 mm,  15.1 g
     *      0.03     42 mm sink, 15.2 g          48 mm,  17.0 g
     *
     * The whole cost of the change is on that middle column and it is honest: a 0.5 m
     * square-edged ledge at 38 km/h is hitting a wall, and the old number bought its 9.6 g
     * by letting the bike ride through the wall. Below 0.05 the remaining sink is bought
     * with peak g on hucks-to-flat, which is the wrong side of that trade.
     *
     * `hardContactSpan` is how far the hillside spring keeps rising before it is flat (the
     * per-wheel `maxWheelForce` ceiling takes over anyway). It was a bare 0.3 in physics.ts.
     */
    hillsideStart: 0.05,
    hardContactSpan: 0.3,
    /**
     * The travel at which the CoM sits exactly `bike.comHeight` above the ground — the
     * kinematic reference the solver measures travel against, not a target. It should
     * agree with the sag the springs actually make (24% front, 28% rear), or the bike
     * rides that many millimetres off its nominal height. reset() derives its pose from
     * load/rate rather than from this number precisely so the two disagreeing is a ride
     * height error and never a spawn that starts on the bump stop.
     */
    sagFraction: 0.26,
    /** How fast an unloaded wheel extends back down, m/s. */
    topOutRate: 2.4,
    /**
     * Numerical guards on a single wheel, N: the suspension part (which loads the tire
     * and makes moments) and the total including the hillside term.
     *
     * These used to be 4200/5600 and they were doing the shaping — see the long note on
     * the ramp above for why that was the wrong tool and what it cost. They are sized to
     * sit *outside* everything the springs can make, so nothing short of driving the axle
     * into a cut bank ever reaches them, and when it does they are only preventing a
     * single bad step from becoming an infinity. If a capture ever shows a wheel pinned at
     * either of these numbers again, the ramp is wrong, not the guard.
     *
     * 5000/7000 -> 6200/8200, because after this round the stack really does exceed 5 kN
     * and the bench caught the guard doing exactly what this comment forbids. With the
     * mid-stroke plateau, at the end of the rider's stroke a wheel makes 1666 + 1200 + 200
     * + 1400 + 1200 = 5.7 kN front and 6.3 kN rear, so at 5000 the guard was truncating
     * the last third of the rider stage — the "a clamp is not a spring, so the stroke past
     * it is free" failure at a new depth. Measured: pinned at 5000 on the deepest excursion
     * in the plunge, and lifting the guard past the stack took that excursion from 317 mm
     * to 276 mm and its episode from 279 ms to 258 ms.
     *
     * 6200/8200 -> 10000/13000, and the capture caught the same failure a third time. The
     * round-3 bump stop makes 1666 + 1050 + 200 + 2700 + 1800 = 7.4 kN front and 8.1 kN
     * rear at the end of its stroke, so 6200 was again truncating it — and this time the
     * consequence was not a statistic. Measured on `braking`, the front wheel landed into
     * a rising cut at 24 km/h, the demand hit 6200 at 91 mm past full travel and *stayed*
     * there for 140 ms while the wheel sank to 338 mm and the bike went over the bars; the
     * previous round's softer stage never reached the guard and rode the same feature out
     * at 190 mm and 21 km/h. Eighteen of twenty-five riding scenarios crashed at the same
     * 3.08 s station because of it. A clamp is not a spring: the stroke past it is free,
     * which is the exact sentence the note above is made of.
     *
     * The earlier argument against raising it — that at 8000 one catastrophic landing read
     * 19.4 g instead of 13 — was measured on a model where the rider was not a load path,
     * and it is no longer the binding consideration: those events are now bounded by
     * `rider.legStiffness` and `legDamping` long before the guard is anywhere near.
     */
    maxNormalForce: 10000,
    maxWheelForce: 12000,
  },

  /**
   * What an impact the suspension could not absorb costs.
   *
   * This block is the second axis, and it exists because three rounds of shaping the
   * spring curve proved there was only one. Sweeping the whole suspension block — support
   * size and placement, both rates, the ramp, the rider stage, low- and high-speed
   * damping, the quadratic, both blow-offs, rebound, the rebound floor, the wheel envelope
   * — every single point that took time off the bump stop paid for it in peak g, and every
   * point that took peak g off paid for it in time on the stop. The curve is on its Pareto
   * front. `frontSupport 820/940` buys 0.5 g at drop 5 and costs 0.7 pp of stop time and a
   * 467 ms worst episode; `compDeepFade 0.7` buys 0.24 pp of stop time and costs 1.1 g at
   * drop 6. There is no shape that is better at both, because the bike has 170 mm of stroke
   * and the mountain hands it 12 m/s landings.
   *
   * The reason that was the only axis is that bottoming had no consequence. Measured: a
   * 20.5 m/s touchdown off a **23 m cliff** buried the rear axle 723 mm past full travel,
   * read 13 g into a crash model that is angle-only, and rode away at 51 km/h. Frequent
   * *and* free. Once hitting the stop costs the player a case — speed, balance, and the
   * rider's hands for a beat — "rare" stops having to be bought with spring force, which
   * is the same currency as harshness.
   *
   * Calibrated against the capture set, not invented: over 164 landings across all 25
   * scenarios the touchdown distribution is median 6.0 m/s, p90 13.6, max 21.0, and the
   * *stroke* those produce is what these numbers are cut against.
   */
  impact: {
    /**
     * Stroke past full frame travel, m, at which a landing starts to be a case.
     *
     * 100 mm -> 30 mm, and the threshold means the same thing it always did — the scale it
     * is measured on changed. What lives past full travel used to be a 580 mm stand-in for
     * the rider's whole body, so 100 mm into it was an ordinary hard landing. It is now a
     * 220 mm bump stop with the real rider elsewhere (see `rider.riderMass`), and 30 mm
     * into *that* is the same physical event: on the drop bench the frame's stroke runs
     * out at 5.5 m/s (a 1.5 m drop, which a 170 mm bike uses all of and rides away from),
     * 30 mm past it is a 7 m/s touchdown and a 2.5 m drop to flat. Everything up to 2 m
     * still scores zero, which is the point — ordinary hard landings must stay free or the
     * mechanic is a tax on riding.
     */
    caseOverTravel: 0.03,
    /**
     * Felt g at which the *g* branch of severity opens, and where it saturates.
     *
     * A backstop, not the main measure. Its job is the hit that makes enormous force
     * through almost no stroke — a cut bank, the hillside term firing on a wall strike —
     * which the stroke branch scores as nothing because the wheel never got the chance to
     * move. 9 g is above every clean landing the capture set contains at full travel — with
     * the rider on their own load path the 5 m/s drop bench now reads 7.1 g and does not
     * reach the stop at all, and the first touchdown that does (6 m/s, a 1.8 m drop) reads
     * 9.6 g with 12 mm of overtravel. That is deliberately right on the line: at that point
     * the springs are making 7 kN and a hit still arriving is one they did not stop.
     */
    caseG: 9.0,
    /**
     * g-seconds above `caseG` that on their own amount to a full-severity hit.
     *
     * The g branch is an impulse and not a peak because a peak cannot tell a hit from a
     * numerical excursion: at 240 Hz ordinary riding contains single-step spikes past 9 g
     * that the 30 Hz telemetry never sees, and read as peaks every one of them scored a
     * case — measured, the `corner` run cased at 1.6 s and never got past 12 km/h against
     * 44 in the same tree with the branch disabled. An impulse is also simply the right
     * quantity: it is the velocity change the hit put through the rider, so 4 ms of 12 g
     * is worth nothing and 40 ms of 15 g is worth everything.
     *
     * 0.42 g*s = 7 g held for 60 ms, which is a wall. A hard clean landing (the 8 m/s drop
     * bench, peaking at 12 g) spends ~25 ms above 9 and scores 0.2 on this branch against
     * 0.44 on the stroke branch, so the stroke branch stays the primary measure and this
     * one only decides the cases stroke cannot see.
     */
    crashGImpulse: 0.42,
    /**
     * Fraction of frame travel past which a wheel counts as "the suspension has run out"
     * for the g branch. Below it the g branch does not accumulate at all — its whole job
     * is the hit that makes enormous force through no stroke, and force made with the
     * stroke still available is what the springs are for.
     */
    deepFraction: 0.9,
    /**
     * Severity at which the rider is on the floor. 1.0 — i.e. the wheels asked for the
     * rider's entire `stopTravel` of leg stroke and kept going, which means the chassis
     * is where the hillside is. There is no attitude at which that is ridden out, and the
     * angle-only crash test sees none of it because the bike arrives flat and leaves flat.
     *
     * Across the capture set this fires 9 times in 25 scenarios and every one of them is a
     * fall of 7 m or more onto raw hillside. It is not a difficulty knob: turning it above
     * 1 means nothing crashes, because severity is clamped there.
     */
    crashSeverity: 1.0,
    /**
     * Seconds the impact window stays open after the bike comes back inside its stroke.
     * Long enough to hold one landing's two or three separate wheel events together as
     * the single case a rider would call it, short enough that two hits a third of a
     * second apart score separately.
     */
    holdTime: 0.18,
    /**
     * Speed a full-severity case takes out, as a decay rate in 1/s applied for as long as
     * the case lasts. 4.5 over a ~0.25 s case is about 68% of the bike's speed, which is
     * what casing a jump does — you stop almost dead and pedal out of it. Ordinary
     * landings are severity 0 and pay nothing.
     */
    caseScrub: 4.5,
    /** Seconds the rider needs to gather themselves up after a full-severity case. */
    caseRecover: 0.85,
    /**
     * Fraction of the rider's authority a full-severity case removes — the balance
     * assist, the lowside save, the manual and the loop-out save all scale by it.
     *
     * 0.55 and not 1. A rider who has just cased is fighting, not absent, and at zero
     * authority every big hit becomes an unrecoverable lowside a beat later, which reads
     * as the game taking the bike away twice for one mistake.
     */
    caseAuthorityLoss: 0.55,
    /**
     * The share of that loss the *save* pays. 0 means the lowside save keeps full
     * authority through a case; set it to 1 to reproduce what this replaced.
     *
     * The finesse terms — holding a commanded lean, a manual — are exactly what a rider
     * loses for a beat after being slammed, and they still pay the full 0.55. The save is
     * not finesse, it is the reflex that stops you going down, and taking that away is
     * what turned "the landing was hard" into "and then it lowsided". Measured: the case
     * that rolled the bike to -59 degrees on `fullrun` also drove `caseShock` to 1, so the
     * save that exists to catch exactly that arrived at 45% strength.
     */
    caseSaveAuthorityLoss: 0,
    /** Where a full-severity case throws the rider fore/aft, m. Positive is at the bars. */
    caseThrow: 0.16,
    /** Roll rate a full-severity case adds, rad/s, to the side the bike was already on. */
    caseBuckRoll: 1.6,
    /**
     * Leg compression velocity a full-severity case drives, m/s.
     *
     * 4.0 -> 1.4. This used to be purely cosmetic — the leg coordinate carried no load, so
     * this was the animation of a case and nothing else. Since round 3 the coordinate is a
     * real load path (`rider.legStiffness` / `legDamping`), and 4 m/s against 1600 N*s/m is
     * 6.4 kN through the contact patches, which is more than the hillside term and would
     * make a case fire the bike into the ground rather than bog it into the hit. 1.4 m/s is
     * still an unmistakable slam — 2.2 kN, about the weight of the whole bike and rider
     * twice over — and it is now a real one.
     */
    caseBodySlam: 1.4,
    /** Speed decay while down, 1/s. A crashed bike and rider slide and stop. */
    crashDrag: 3.5,
    /** Seconds on the floor before the rider picks the bike up and gets back on. */
    crashRecoverTime: 2.4,
    /** Seconds after a remount during which the impact path cannot crash again — the
     * bike is put back on the ground and the first step must not read as another hit. */
    remountGrace: 0.5,
  },

  tire: {
    /** Peak longitudinal friction coefficient on hardpack, at the reference load (half
     * the bike's weight on one wheel). Actual mu falls with load — see loadSensitivity. */
    muLongPeak: 1.25,
    muLatPeak: 1.18,
    /**
     * Load sensitivity: mu scales as (load / halfWeight)^-loadSensitivity.
     *
     * Every real tire has this and this model did not, which made cornering force exactly
     * proportional to load. That is not a small simplification when load is the thing a
     * landing spikes: measured on holdline, a touchdown put five times static load through
     * each patch, the side force went up fivefold with it, and the bike was stood up
     * through vertical and rolled out the other side to -58 degrees against a +29 degree
     * lean command.
     *
     * 0.11 is at the gentle end of what is measured on real rubber (0.1-0.3 over this
     * range) and is deliberately so — this is a correction to a missing effect, not a
     * handling knob. At five times reference load it takes 17% off mu. It started at 0.16
     * and came down because the effect cuts both ways and the capture showed both: it is
     * what stops a landing standing a leaned bike up through vertical, and it is also less
     * side force for the front tire to catch a bike that is falling *into* a corner it
     * landed in. 0.16 fixed the first and made the second measurably worse on the corner
     * slalom. The floor and
     * ceiling stop a nearly unloaded wheel becoming a magnet and a very heavily loaded one
     * losing grip faster than it gains load, either of which would be worse than the
     * proportional model it replaces.
     */
    loadSensitivity: 0.11,
    loadSensMin: 0.78,
    loadSensMax: 1.2,
    /** Sliding (post-peak) coefficient as fraction of peak. */
    slideRatio: 0.72,
    /** How abruptly force collapses once past the friction ellipse. Low = progressive
     * and catchable, high = binary. This number is "is the slide legible". */
    slideFalloff: 1.6,
    /** Slip ratio at which longitudinal force peaks. */
    peakSlipRatio: 0.13,
    /** Slip angle at which lateral force peaks, rad. */
    peakSlipAngle: 0.16,
    /**
     * Camber thrust coefficient. Without this, leaning produces no turn, nothing
     * balances gravity, and the bike always falls over. Modelled as an equivalent
     * slip-angle offset so it shares the one friction circle with braking.
     *
     * The magnitude is load-bearing. Camber alone exactly holding a small lean happens
     * at 2*peakSlipAngle/(pi*muLat) = 0.081. Above that, lean over-produces side force,
     * the bike snaps upright, overshoots through vertical, and diverges within seconds.
     * Sitting just under it leaves the rest to the slip angle the turn itself generates,
     * which is what gives lean a stable equilibrium.
     */
    camberThrust: 0.062,
    /** Tire relaxation: the speed floor used when converting contact-patch velocity into
     * slip. Without it the slip gain goes as 1/v and the explicit integrator rings
     * itself apart the moment the bike slows down. */
    longRelaxSpeed: 1.4,
    latRelaxSpeed: 2.2,
    /** Bristle stiffness for the stiction anchor, N/m. A velocity damper instead of
     * this lets a parked bike creep down a slope. */
    stictionStiffness: 45000,
    /**
     * And its damper, N*s/m. It only ever sees *slide* velocity — patch minus rolling —
     * so a wheel that is genuinely rolling feels none of it and this is not the creep
     * damper in disguise.
     *
     * 800 was under-damped for the mode it has to hold: two bristles this stiff under a
     * 92 kg chassis put a pitch-on-the-tires mode at 6.3 Hz, and at 800 that mode rang at
     * constant amplitude forever. Measured on a bike parked on a 50% pitch with both
     * levers pulled: g swinging 0.96-1.04 and the lightly loaded rear wheel reporting
     * anywhere from a third to nine tenths of its friction budget, six times a second,
     * while the bike itself did not move a millimetre. Dust and tire sound read that
     * number. At 1600 the same bike sits at exactly 1.00 g and does not move at all.
     */
    stictionDamping: 1600,
    /** Wheel speed above which the bristle model hands back to the slip model, rad/s. */
    stictionSpin: 4,
    stictionBreakaway: 1.15,
    /** Speed below which the bristle model takes over from the slip model, m/s. */
    stictionSpeed: 1.2,
    /** Rolling resistance coefficient, divided by the surface's own roll multiplier. A
     * knobby enduro tire at trail pressure measures 0.02 on hardpack and 0.04-0.05 on
     * loose dirt, so 0.03 through the surface table lands on the right side of both. */
    rollingResistance: 0.03,
    /**
     * How fast an airborne wheel loses its spin, 1/s. Bearing drag and the tire's own
     * windage, and nothing else — so this is a ~20 s time constant, not a 3 s one.
     *
     * 0.35 was the number and it is the single most expensive mistake in the file. This
     * mountain puts the bike in the air for a second at a time; at 0.35 the wheel came
     * down 30% slow, which is slip ratio -0.3, which is *past the peak of the braking
     * curve* at whatever load the landing happens to make. Measured: a 22 km/h touchdown
     * generated 1680 N of rear braking force nobody asked for and the bike was stopped
     * two seconds later. A wheel in the air keeps spinning; that is why you can land
     * cross-rutted and still have drive.
     */
    freeSpinDecay: 0.05,
    /** How fast the published slipUsage tracks the instantaneous value, 1/s. This is the
     * number dust, tire sound and the HUD all read, so it wants to describe the slide, not
     * the solver. 25/s is a 40 ms constant — a fifth of the time a real slide takes to
     * build, and enough to stop a near-unloaded wheel's friction budget flickering. */
    slipUsageSmoothing: 25,
  },

  rider: {
    /** How far the rider can shift weight fore/aft from neutral, m. */
    weightShiftRange: 0.28,
    weightShiftRate: 3.2,
    /**
     * Automatic fore/aft bias: how far the rider moves on their own, m, and at what
     * longitudinal acceleration (in g) they reach the end of it. This is the rider
     * doing the riding — forward on the gas, behind the saddle on the brakes — and it
     * is what keeps the front wheel inside its sag window through a hard transition
     * instead of topped out. Keep the range well under weightShiftRange: past that the
     * player's own weight shift stops meaning anything.
     */
    autoBiasRange: 0.11,
    autoBiasSpan: 0.55,
    autoBiasRate: 4.0,
    /**
     * The steepness half of the same bias: how far back the rider gets on a descent, m,
     * and the ground pitch (rad) at which they are all the way there. Sized so that at
     * 34 degrees the combined CoM has moved far enough back to keep the front contact
     * patch ahead of it — below that the bike is past its own endo balance point from
     * terrain alone, which is precisely why a rider gets behind the saddle on steeps.
     */
    autoBiasSteepRange: 0.17,
    autoBiasSteepSpan: 0.6,
    /** Fraction of that shift the combined CoM actually moves (rider mass / total). */
    comShiftFraction: 0.8,
    /** Max lean the rider can command, rad. */
    maxLean: 0.68,
    /**
     * How fast commanded lean tracks the stick, 1/s. 3.6 -> 7.2.
     *
     * This is a filter on the *setpoint*, not on the bike, and at 3.6 it was a 278 ms time
     * constant sitting in front of a machine that already has all the lag it needs. The bar
     * asks for input to visible response inside ~80 ms; a quarter of a second of it was
     * being spent before the countersteer loop had even been told what to aim at.
     *
     * What it cost, traced at 240 Hz through the first ejection of the gate scenario — the
     * one that sets `completionPct`, because that number is distance before the first
     * terminal failure and nothing else. At 13 m the trail turns; the autopilot sees it and
     * ramps its stick to 85% of full lean by t=4.7 s. `leanCommand` is still at 14 degrees
     * of the 23 it was asked for, reaches 20 at t=5.0, and does not arrive until t=6.0 —
     * **1.3 s after the stick**. The bike's own lean tracks the command to within a degree
     * the whole way (p50 error 1.0 deg over the run), so none of that delay is the chassis:
     * it is this filter, in series with the rider's own 80 ms and slew rate. In those 1.3 s
     * the bike travels 8 m and goes from 0.03 m to 3.99 m off the centreline, and 2.5 m is
     * an ejection.
     *
     * Raising it cannot make the bike twitchy, and that is worth stating because it is the
     * usual objection. `updateSteering` closes on lean error through a **rate** loop whose
     * rate is capped at `maxRollRate` — a faster setpoint only means the loop saturates
     * that cap sooner, so the fastest the bike can roll is unchanged and countersteer still
     * has to earn every degree. 7.2 is a 139 ms constant: still a rider's hand rather than
     * a step, and now the smaller half of the delay rather than the larger.
     *
     * What it is worth, measured in-session (`corner`, 1517 steady-cornering samples at
     * 240 Hz, against physics.ts's OWN roll equilibrium rather than the rigid-bicycle
     * formula — see the note in the probe): the bike delivers **96.4% of the path curvature
     * its lean asks for, against 81.7% before**, and the number of steps that qualify as
     * steady cornering at all goes 92 -> 1517, because the bike now settles into a corner
     * instead of chasing it. On `fullrun` the aggregate moves with it: average speed 19.5
     * -> 20.5 km/h, distance actually ridden 1042 -> 1081 m, stalls 3 -> 2, airborne 0.84
     * -> 0.70% of steps.
     *
     * The honest cost, and it is a real one: `holdline` and `corner` are open-loop constant
     * inputs on raw mountain, and their *distance* numbers are chaotic — a bike that turns
     * 100 ms earlier is on a different piece of hillside a second later. `corner` reads
     * 39.9 m at 3.6, 46.9 m at 5.4 and 125.4 m at 7.2; `holdline` reads 158 m at 3.6 and
     * 70 m at 7.2, ending stopped in a terrain notch it has no input left to pedal out of.
     * Those are three samples of a chaotic map, not a trend, and the file already warns
     * against reading them as one. The distributional numbers above are what moved.
     */
    leanCommandRate: 7.2,
    /**
     * A bike is leaned by countersteering, not rider torque — a rider has nothing to
     * push against. This gain converts lean error into a steer command; tire forces
     * then produce the roll moment. Full stick is ~5 degrees of opposite bar, which is
     * roughly a 0.4 s roll into a corner. Anything an order of magnitude larger (the
     * "make it responsive" instinct) commands half a turn of lock, saturates the front
     * tire and throws the bike on its side.
     */
    countersteerGain: 0.13,
    /** Lean error -> desired roll rate, 1/s, and the cap on that rate, rad/s. Capping it
     * is what keeps a full-stick flick from outrunning the tire that has to catch it. */
    leanRateGain: 6.0,
    maxRollRate: 0.85,
    /** Self-steer (caster) gain on the balance-derived steady-turn steer angle. */
    selfSteerGain: 1.0,
    /**
     * Rider balance. Below balanceSpeed a two-wheeler is a bare inverted pendulum — the
     * contact patches cannot generate a righting moment when they are not moving — so
     * the rider holds it up with body english and a dab. At speed it is capped hard at
     * balanceMaxTorque, which is about all a rider's own mass on a metre of lever is
     * worth: raise that and the bike rights itself without turning, and countersteer
     * stops mattering at all.
     */
    balanceSpeed: 3.4,
    balanceTorque: 1500,
    balanceDamping: 420,
    /** Ceiling on the balance torque at speed (rider mass on a ~1 m lever) and at a
     * standstill (where a foot down is available), N*m. */
    balanceMaxTorque: 340,
    balanceMaxTorqueLow: 1600,
    /** The lowside save: how far past the commanded lean before the rider starts
     * fighting (rad), and how hard they fight per radian past it (N*m/rad). Keep the
     * deadband generous — inside it the tires must do the leaning on their own. */
    saveDeadband: 0.08,
    saveTorque: 3200,
    saveDamping: 900,
    /**
     * How much load the save is allowed to work against, as a multiple of the bike's own
     * weight. Set to 1.0 to reproduce the behaviour this replaced.
     *
     * Every rider term here is scaled by `loadFrac`, which is `clamp01(load/(m*g))` — and
     * that clamp is an asymmetry the model could not survive. The torque that topples a
     * leaned bike is the normal force acting through `comHeight*sin(lean)`, and it scales
     * with the *whole* load: on a landing that is eight times static. The rider's answer to
     * it was capped at its one-g value, so during exactly the 60 ms when the bike is being
     * rolled over hardest, the rider had a twentieth of the authority the thing rolling
     * them had. Measured on `fullrun` at 240 Hz: a landing at 8.47 s put 7.2 kN through the
     * rear patch at 38 degrees of lean and rolled the bike to **-59 degrees against a
     * steady -33 degree command** — 26 degrees past what the rider asked for, from a
     * landing they were still holding a corner through.
     *
     * It is not a cheat, it is the same physics on both sides of the equation: a rider
     * standing on the pedals through a compression can push sideways in proportion to how
     * hard the ground is pushing up at them, which is precisely why weighting the outside
     * pedal works in a berm and does nothing in the air. Capped at 3 so it stays a rider
     * and not a gyroscope, and applied ONLY to the save — the ordinary balance PD keeps
     * its one-g ceiling, so nothing about steady cornering changes.
     *
     * Priced by A/B at 240 Hz (this and `impact.caseSaveAuthorityLoss` together, everything
     * else held): on `fullrun` the worst lean past the command falls 35.0 -> 28.2 degrees
     * and p99 25.1 -> 18.8, while the bike covers 1237 -> 1510 m of the run at 15.0 ->
     * 18.3 km/h. On `holdline` the p90 goes 4.8 -> 4.2 degrees. Its cost is `corner`, which
     * covers 134 m instead of 103 m in 23 s because the bike is held closer to the lean it
     * was asked for and therefore actually turns.
     */
    saveLoadCap: 3.0,
    /**
     * Roll authority while the tires carry (almost) nothing.
     *
     * Every rider term above is scaled by load, correctly — a foot down needs ground. That
     * left a hole between a bottom-out and the tires taking load again: the wheels are
     * touching, carrying nearly zero, so the grounded rider has no authority, and the
     * airborne path is not running because the bike is technically in contact. Two hundred
     * milliseconds of nobody flying the bike, at exactly the moment a landing is most
     * likely to put it on its side.
     *
     * `unweightedLoadFrac` is the share of the bike's weight below which this fades in
     * (squared, so the onset is gentle and ordinary rough-ground unweighting barely sees
     * it). The gain and ceiling are sized against what applyAirControl already grants a
     * rider with no tire load at all, because that is the same rider doing the same thing.
     */
    unweightedLoadFrac: 0.45,
    unweightedRollGain: 620,
    unweightedRollMax: 780,
    /**
     * The endo/loop-out save: pitch *relative to the ground under the bike* past which
     * the rider fights it (rad), and how hard (N*m/rad, N*m*s/rad). 0.5 rad is already a
     * big, rideable stoppie with the rear wheel well clear — below it, nothing.
     *
     * It must start inside atan(frontOffset/comHeight) = 0.58 rad, which is where the
     * front contact patch passes behind the CoM and the front wheel's own normal force
     * starts pitching the bike further over. Outside that angle the endo is a stable
     * equilibrium, and a save that only engages there arrives too late to matter.
     */
    pitchSaveAngle: 0.5,
    /**
     * The same save in the other direction, and it has to fire earlier. Nose-up has its
     * own balance point and it is much closer in: the *rear* contact patch passes in
     * front of the CoM at atan(rearOffset/comHeight), and rearOffset is the short arm —
     * shorter still once the steepness bias has the rider behind the saddle, which is
     * exactly when a descent is trying to loop the bike out. On a 26 degree pitch that
     * lands at 0.38 rad, so a save that waited for 0.5 rad was always late, and the
     * harness caught the consequence: cresting a roller the bike went to 26 degrees
     * nose-up with the front wheel in the air, came down on the fork, and stopped.
     *
     * It is a threshold, not a ceiling — the torque grows from zero past it — so a
     * manual is still a manual, it just stops being a one-way trip.
     */
    loopSaveAngle: 0.38,
    pitchSaveTorque: 7000,
    pitchSaveDamping: 1400,
    /** How far the rider's mass can move sideways, m — published as riderLat. */
    lateralShiftRange: 0.38,
    /* --- The rider's legs, as a load path ------------------------------------------
     *
     * Round 3's structural change, and the thing two rounds of spring shaping could not
     * substitute for. Before this, `bodySquash` was a 28 kg mass on a spring driven
     * *one way* by frame acceleration: it moved for the animation and carried no load,
     * so the suspension had to stop the whole 92 kg inside 165 mm of frame stroke. That
     * is 10 g at 5.7 m/s no matter how the curve is shaped — the bound is v^2/(2*d*g)
     * and the only free variable in it is d.
     *
     * A real bike does not work that way. Roughly two thirds of the mass is a rider
     * standing on the pedals, connected to the frame through legs and arms with a
     * quarter-metre of their own travel. The suspension carries whatever *that* element
     * transmits, and a rider absorbing a landing transmits far less than their own
     * inertia would. This is why a 170 mm bike eats a 1.5 m drop to flat and a 170 mm
     * spring on its own cannot.
     *
     * So the vertical dynamics are now two masses: `riderMass` on this element, the rest
     * on the chassis and therefore on the springs. Horizontal motion is still one rigid
     * mass — a rider is stiffly coupled fore/aft and laterally, and `riderFore` /
     * `riderLat` already carry the slow part of that.
     *
     * Everything below the threshold where the legs give is unchanged by construction:
     * at static load the element sits at zero deflection making exactly riderMass*g, so
     * sag, small-bump compliance, brake dive, cornering and the parked bike are all bit
     * -for-bit what they were.
     */

    /**
     * The mass hanging on the legs and arms, kg, out of `bike.mass`.
     *
     * 62 of 92. A 75 kg rider's torso, head, upper legs and upper arms are what the legs
     * actually support; feet, shins, forearms and hands move with the bike, and so does
     * all 15 kg of it. The remaining 30 kg is the chassis mass the springs see directly.
     *
     * It is the single most load-bearing number in this block, because the ratio sets how
     * much of a landing the springs must take on their own. Swept 52/62/70 on the drop
     * bench: 52 leaves too much on the springs (drop-5 still bottoms), 70 leaves the
     * chassis so light that the fork tops out under its own spring between hits.
     */
    riderMass: 62,
    /**
     * Leg stiffness about the held position, N/m, and damping, N*s/m.
     *
     * 7800 N/m puts the rider's own bounce frequency at 1.8 Hz on 62 kg, which is where a
     * standing rider's knees-and-elbows mode actually sits. Stiffer and the legs stop
     * being a load limiter, which is the whole point — at 11000 the plunge's deepest
     * excursion past full travel goes 15 mm to 83 mm; softer and they stop supporting at
     * all, and at 6000 the washboard set puts 0.33 s back on the bump stop.
     *
     * The damping is the number that matters most for how a landing *reads*, and it is
     * larger than the spring would suggest on purpose: legs absorbing a hit are working
     * eccentrically, which is almost entirely dissipative, so this element is a damper
     * with a spring rather than a spring with a damper. 1600 N*s/m is about twice critical
     * on the relative coordinate. Swept 900/1150/1450/1600/1750 against the plunge, the
     * deepest excursion reads 47/26/24/15/20 mm and the worst episode on the stop
     * 138/83/79/50/58 ms. It is also where the bounce went: drop-bench coefficient of
     * restitution is 0.17 against round 2's 0.31, and that energy is now dissipated in the
     * rider instead of stored in the end of the fork stroke and handed straight back.
     */
    legStiffness: 7800,
    legDamping: 1600,
    /** Compression limit, m — the crouch running out. `legEndForce` over `legEndSpan`
     * is the bone-on-bone rise before it, quadratic so there is no shelf. */
    bodyTravel: 0.26,
    legEndSpan: 0.10,
    legEndForce: 3600,
    /** Extension limit, m: legs and arms straight. Reached in the air, where the element
     * carries nothing and both masses simply fall together. */
    legExtend: 0.07,
    /**
     * How much of the momentum in the relative coordinate survives hitting either end of
     * it, 0..1. 1 is the physical answer and is what ships; 0 reproduces the behaviour
     * this file had until round 9, and it is only a number so the A/B can be run inside
     * one browser session on a mountain nine other agents are editing.
     *
     * See `legStopHit` in physics.ts. `bodySquashVel` is the relative velocity and the
     * rider has no velocity state of their own, so zeroing it at a stop does not stop the
     * two masses — it teleports 62 of the system's 92 kg onto the chassis's velocity, with
     * no force and no reaction. At the extension stop that deletes the rider's upward
     * momentum at the exact instant their legs go taut, which on a bike is the mechanism
     * that lifts the frame off the ground.
     */
    legStopMomentum: 1,
    /** Numerical guard only, N. Sits far outside anything a rider makes. */
    legMaxForce: 11000,
    /**
     * How far the pump command moves the datum the legs spring about, per metre of
     * commanded leg extension (`pumpPos`, which runs to `control.pumpRange`).
     *
     * This used to be 0.8 and it was purely cosmetic — the legs carried no load, so
     * moving their datum moved the pose and nothing else, and the whole pump impulse came
     * from `control.pumpMass` acting at the contact patches. Now the datum is a real
     * preload: 0.16 m of pumpRange against 8600 N/m would be 1.4 kN of extra patch load
     * on its own, which is a second pump stacked on the first.
     *
     * 0.9 * 0.16 m against 8600 N/m is a peak of 1.24 kN, about two body weights through
     * the legs, which is what a rider actually makes in a hard pump and is self-limiting
     * anyway: pushing the datum down accelerates the rider up until the legs are back on
     * it, so there is no way to hold it.
     *
     * Swept 0.5/0.7/0.8/0.9/1.0/1.15 against the three deterministic roller benches with
     * the old `control.pumpMass` stand-in removed. Distance gained by pumping, against
     * round 2's +9.0 / -0.2 / +17.1 m: 0.8 reads +0.8/+14.1/+19.2, 0.9 reads
     * +8.9/+35.2/+25.3, 1.0 reads +22.1/+49.1/+31.1. 0.9 is the lowest value that beats
     * round 2 on all three, which was the constraint — the pump is the one mechanic in
     * this file that was verified working and it may not regress.
     */
    pumpLegDatum: 0.9,
  },

  control: {
    maxSteerAngle: 0.62,
    steerRate: 22,
    /** Countersteer authority falls off with speed to keep high-speed stable. */
    steerSpeedFalloff: 0.03,
    /** Speed floor, m/s, in the v^2 of the steady-turn steer angle. */
    steerSpeedFloor: 2.6,
    /** Ceiling on the caster/steady-turn steer angle, rad. */
    maxCasterSteer: 0.3,
    /** Steering angle assumed available for holding a lean, rad — sets how much lean the
     * bike will accept a command for at a given speed. */
    maxUsefulSteer: 0.18,
    /** Ceiling on the countersteer contribution, rad. Past ~6 degrees the front tire is
     * already at peak slip angle and more bar buys nothing but a washout. */
    maxCountersteer: 0.13,
    /** Below this, the bars are steered directly rather than by leaning, m/s. */
    lowSpeedSteerSpeed: 4.0,
    /**
     * Brake torque at full lever, N*m. The tire can transmit roughly 1.25 * load * R,
     * and measured, the front carries ~950 N under hard braking, so ~440 N*m is where
     * the front actually locks: full lever is a genuine lock-up and the useful range is
     * spread across the whole lever rather than arriving in the first 10%.
     *
     * 620 was past that by half again, which made anything over about 40% lever an
     * instant lock — including the harness's 45% trail-brake, which welded the front
     * wheel during a standing start and stopped the corner run before it began. A brake
     * whose top half is all lock-up is not modulatable, which is a bar item in itself.
     * The rear is deliberately still easy to lock: it carries ~280 N under the brakes,
     * so it lets go at about a third of the lever and slides, which is what it is for.
     *
     * 470 was still too much, and the measurement that shows it is a lever sweep rather
     * than a lock-up test. Held at a steady 14 m/s on the flat bench, the front reached
     * its *endo* limit — the point past which more lever cannot slow the bike, only lift
     * the rear — at 60% of travel, and locked at 87%. So the top 40% of the lever bought
     * nothing but a locked wheel: the useful band, the one between "slowing" and "the
     * rear is coming up", was squeezed into the first six tenths. At 390 the endo point
     * moves to 72% of lever and lock-up to 95%, which spreads the same authority over
     * half again as much travel, and full lever is still a genuine lock-up rather than a
     * soft stop. Light and mid-lever braking is only ~17% weaker, which is inside what
     * the automatic rider bias absorbs. The rear comes down with it so the balance
     * between the two levers does not change.
     */
    brakeFrontMax: 390,
    brakeRearMax: 230,
    /**
     * Lever-to-torque exponent. 1.0 is a linear lever, and a linear lever is not a
     * modulatable brake.
     *
     * The useful part of a front brake is the band between "slowing hard" and "locked",
     * and on a linear map that band is crammed into the top few percent of the lever
     * because torque demand grows with the square of nothing while grip is flat. Above
     * 1 the map is progressive: the first half of the lever is fine control, the second
     * half is where the stopping happens, and lock-up lives at 96% of travel instead of
     * arriving as a step. This is also what real levers do, through pad taper and hose
     * expansion — nobody has ever ridden a linear brake.
     *
     * It is now actually read. `updateBrakes` had it inlined as `l * Math.sqrt(l)`, so
     * this number documented a knob that did nothing and the single most important
     * control curve in the file could not be swept. 1.5 is exactly what the inlined code
     * did, so wiring it up changed no behaviour — it only made the number honest, and it
     * is what let the brakeFrontMax sweep above happen at all.
     */
    brakeCurve: 1.5,
    /**
     * Time constant for the pads actually taking up the lever, s. Pad clearance, hose
     * swell and the tire's own carcass all mean a step at the lever is a ramp at the
     * contact patch. Without it a scripted (or keyboard) step input arrives as an
     * instantaneous 470 N*m, which locks the wheel before the load has transferred and
     * turns every stop into a skid. Short enough that the response is still inside the
     * 80 ms the bar asks for.
     */
    brakeRampTime: 0.055,
    pedalPowerMax: 420,
    /** Torque cap so a standing start is a sprint, not a launch, N*m. */
    pedalTorqueMax: 170,
    /** Impulse from a full hop, N*s. 330 over 92 kg is 3.6 m/s, a ~0.65 m bunny hop. */
    hopImpulse: 330,
    hopChargeTime: 0.42,

    /* --- Manual ------------------------------------------------------------
     * A manual is the rider pulling on the bars against the pedals with the front wheel
     * off the ground, held on the balance point. It is not the same thing as being
     * behind the saddle — you can do either without the other — so it gets its own axis
     * rather than being folded into weight shift.
     */
    /** Pitch attitude, relative to the ground under the bike, that a full manual asks
     * for (rad). 26 degrees is a real, holdable manual with the front wheel well up. */
    manualAngle: 0.46,
    /** Proportional gain (N*m/rad) and damping (N*m*s/rad) with which the rider holds
     * that attitude, and the ceiling on the pair. The ceiling is what makes a manual a
     * balance point rather than a switch: the rider has roughly their own weight on the
     * bar-to-pedal lever, and no more, so overshooting past the balance point is
     * something they have to catch by moving, not something the torque fixes for them. */
    manualTorque: 780,
    manualDamping: 260,
    manualTorqueMax: 520,
    /** How much of the loop-out save is stood down while a manual is commanded, rad. The
     * save exists so the bike does not loop out from terrain the rider did not ask for;
     * a rider deliberately manualling is asking for exactly that attitude, and a save
     * that fights them means the mechanic does not exist. Past this it engages again. */
    manualSaveRelief: 0.2,

    /* --- Pump --------------------------------------------------------------
     * Pumping is the rider driving their own mass up and down against the bike. It adds
     * speed for one reason and it is worth being precise about it, because a version
     * that adds speed for any other reason is a cheat that will read as one.
     *
     * The ground reacts along its own normal. On a downslope that normal tilts *forward*
     * (n = (sin@, cos@) for a surface descending in +x), so any extra load the rider puts
     * through the contact patch there has a forward component. On an upslope it tilts
     * backward, so unloading there removes a retarding component. Push on the backside of
     * a roller, absorb over the face, and the two halves of the same leg stroke both push
     * you along the trail. On flat ground the same stroke nets exactly zero, and holding
     * the button down does nothing at all, because a rider's legs have finite travel and
     * a sustained internal force has no reaction to push against.
     *
     * That last point used to be enforced by construction: the load was
     * `pumpMass * (acceleration of the leg coordinate)`, injected at the contact patches,
     * so extending and then stopping netted zero impulse whatever the player did. It was
     * a stand-in for a rider the model did not have, and round 3 gave it one — see
     * `rider.riderMass`. The pump is now the rider's own mass on their own legs
     * (`rider.pumpLegDatum` moves the datum those legs hold about), which is both the
     * honest mechanism and *still* unexploitable, for a better reason than construction:
     * pushing the datum down accelerates the rider away from it, so a held button decays
     * to nothing on its own.
     *
     * The stand-in had to go rather than be re-tuned. Nothing balanced it — the ground
     * reaction was a free upward force on the frame — and once the chassis alone was
     * 30 kg instead of a rigid 92 it shoved the bike three times as hard and inverted the
     * mechanic: the round-2 +100 N*s on the roller bench measured -3 N*s.
     */
    /** How far the rider's legs travel on a pump stroke, m. */
    pumpRange: 0.16,
    /** Stiffness (1/s^2) and damping (1/s) with which the rider drives that coordinate to
     * where they want it. ~10.5 rad/s puts a full stroke at about 0.15 s, which is what
     * sets the timing window: too early or too late by 150 ms and the push lands on the
     * wrong side of the roller. */
    pumpStiffness: 110,
    pumpDamping: 19,
    /**
     * The same pair for the other direction — the rider *un*weighting, m: stiffness
     * (1/s^2) and damping (1/s). 2000 is 45 rad/s and 85 is about 0.95 critical, which
     * puts a full release at ~50 ms against the push's 150.
     *
     * These exist because a single rate in both directions is what stopped the bike
     * leaving the ground, and the asymmetry is not a fudge — pushing into the bike is a
     * muscle contraction lifting most of the rider's weight, and unweighting is releasing
     * one, which every measurement of human limb dynamics puts several times quicker.
     *
     * What one rate cost, measured at 240 Hz on flat ground with a full-charge hop: the
     * impulse arrives on `s.vy` in a single step while `pumpPos` is still at 0.96 of the
     * preload, so `rider.pumpLegDatum` still holds the leg datum 135 mm below the leg
     * coordinate and the legs are still transmitting 1185 N into a 30 kg chassis. The
     * frame is pressed back into the hill at 4.8 g for the whole flight: 3.6 m/s of
     * impulse, worth 0.66 m and 0.73 s in vacuum, delivered 120 mm and 0.14 s.
     *
     * Swept 800/1400/2000/3200 on the flat hop bench (see the header of physics.ts).
     */
    pumpRetractStiffness: 2000,
    pumpRetractDamping: 85,
    /** Airborne rotation the rider can command, rad/s (targets, not accelerations), how
     * fast the bike reaches them, and how firmly a neutral stick levels it for landing. */
    airPitchRate: 1.6,
    airRollRate: 2.4,
    airYawRate: 2.2,
    airControlRate: 7.0,
    /** How hard airborne roll chases the commanded lean angle, 1/s. */
    airLeanTrack: 3.5,
    airLevelGain: 1.4,
    /** Airborne pitch attitude the stick commands, rad. */
    airPitchRange: 0.6,
    /**
     * How much of the landing slope a neutral stick matches in the air, 0..1. 0 levels the
     * bike to the world horizon, which is what this used to do unconditionally.
     *
     * `docs/BAR.md` asks that "matching the slope is rewarded" on a landing. With this at
     * zero the model actively prevented it: neutral stick rotated the bike toward *world*
     * level, so on any descent it arrived nose-up by the whole gradient, touched down
     * rear-first, and pitched straight back into the air. Every other attitude test in
     * physics.ts is already measured against `groundPitch` and the file says why — on a
     * 30 degree chute an ordinary attitude *is* 30 degrees nose down. Air control was the
     * one place that still used the horizon.
     *
     * Priced by a single-session A/B at 240 Hz (this constant alone, everything else
     * held): on `corner` the bike is **airborne 8.8% of the run with it off and 1.8% with
     * it on**, and the front wheel is off the ground for 10.1% of grounded frames against
     * 6.0%. On `holdline` the worst lean past the rider's command falls 43.9 -> 41.7
     * degrees. On `fullrun` and `descend` it is a wash. Its cost is that `corner` covers
     * 142 m instead of 103 m in the same 23 s — the bike is on the ground turning instead
     * of skipping, which is the trade this is for.
     *
     * 1.0 rather than something less: a rider looking at the landing matches it, and any
     * value below 1 is a bike that is deliberately mis-pitched by the terrain's gradient.
     * The stick still commands `airPitchRange` on top, so a manual or a nose-dive in the
     * air is unchanged — it is now relative to the ground, which is what a player means.
     */
    airGroundTrack: 1.0,
    /**
     * Seconds of flight the landing-slope preview looks ahead, as a ceiling on the
     * ballistic estimate. The preview is "where will I be when I come back to the height
     * of the ground under me", which is exact for flat ground and short for a landing that
     * falls away; the cap keeps a long float from sampling the far side of the valley.
     */
    airPreviewMaxS: 1.2,
  },

  camera: {
    followDistance: 4.6,
    followHeight: 1.62,
    lookAheadTime: 0.42,
    /** Positional smoothing rate, 1/s. Higher = tighter/snappier. */
    positionRate: 7.5,
    rotationRate: 9.0,
    fovBase: 62,
    fovSpeedGain: 0.42,
    fovMax: 88,
    /** Roll the camera a fraction of bike lean for a planted feel. */
    leanCoupling: 0.22,
    shakeGain: 1.0,
    /** Camera collides with terrain and pulls in rather than clipping through. */
    collisionRadius: 0.35,
  },

  world: {
    seed: 20260805,
    /** Terrain patch size, m, and vertex spacing at LOD0, m. */
    patchSize: 128,
    lod0Spacing: 0.5,
    viewDistance: 3200,
    /** Peak elevation of the massif, m. */
    reliefHeight: 940,
  },

  render: {
    shadowDistance: 140,
    shadowMapSize: 2048,
    cascadeCount: 3,
    exposure: 1.0,
    /** Physical-ish sun intensity multiplier. */
    sunIntensity: 3.2,
  },
} as const;

export type TuningShape = typeof Tuning;

/** Mutable overlay for live tuning / sweeps without editing source. */
export const TuningOverrides: Record<string, number> = {};

/**
 * Probe hook: the live tuning object, so an A/B can be run inside ONE browser session.
 *
 * It exists because the alternative is not an A/B. Nine agents edit the mountain while
 * this file is being measured, so two numbers taken from two boots minutes apart differ
 * by whatever the world did in between — physics.ts's header records a round that was
 * nearly routed at the wrong defect for exactly that reason. With this, a probe measures
 * the old value and the new one against the identical heightfield.
 *
 * WARNING, and it is the wave-4 lesson verbatim: `capture.mjs` clears `__`-prefixed
 * globals between scenarios but it does NOT restore the *contents* of one created at
 * boot, so a value written here leaks into every scenario after it. Nothing in
 * `tools/capture/scenarios.mjs` may write to it. Throwaway probes under `captures/` only,
 * which boot their own page.
 */
declare global {
  interface Window { __gripTuning?: TuningShape }
}
if (typeof window !== 'undefined') {
  (window as Window).__gripTuning = Tuning;
}

/** Read a tuning value with dotted path, honouring overrides. */
export function tune(path: string): number {
  if (path in TuningOverrides) return TuningOverrides[path];
  let node: unknown = Tuning;
  for (const key of path.split('.')) {
    node = (node as Record<string, unknown>)[key];
    if (node === undefined) throw new Error(`Unknown tuning path: ${path}`);
  }
  return node as number;
}
